/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import useGetUrlParams from "../../../../../hooks/useGetUrlParams";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import * as React from "react";
import {useEffect, useState} from "react";
import VehicleMeasurements from "../VehicleMeasurements";
import SensorInformation from "../SensorInformation";
import {useSelectRefreshTableCount} from "../../../../../redux/ducks/Wizard/SliceProvider";
import {
  selectVehicleProgramsSelectedDevelopmentStageData,
  setIsConfigSensorReferenceFrameDialogOpen,
  setIsLoadingDevelopmentStageData,
  setVehicleProgramsSelectedDevelopmentStageData
} from "../../../../../redux/ducks/VehicleProgramsDetails/slice";
import {usePostRequest} from "../../../../../api/helper";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../constants";
import {useMsal} from "@azure/msal-react";
import {config} from "../../../../../AppConfig/config";
import {loginRequest, requestIdToken} from "../../../../../auth/authConfig";
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../../hooks/useSetRecentlyViewedWithDeps";
import {ETrackedItemTypes} from "../../../../../constants/trackedItems";
import {useGetCurrentPageUserAccessLevel, useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {IconButton, Tooltip} from "@mui/material";
import {ArgosDownloadIcon, ArgosSettingsIcon} from "../../../../components/Icons/ArgosActionIcons";
import ArgosPopper from "../../../../components/Popper/ArgosPopper";
import {baseBlue, tableButtonGray} from "../../../../../constants/colors";
import ArgosMenuItem from "../../../../components/Form/Select/ArgosMenuItem";
import {ArgosFavoriteIconLogic} from "../../../../components/Buttons/ArgosFavoriteIconLogic";
import {HorizontalTabs} from "../../../../components/Tabs/HorizontalTabs";
import AdasItemDetails from "./AdasItemDetails";
import {checkDownloadMenuItemPermission, EDownloadMenuItemType} from "../../../../../helpers/permissionHelpers";

const RightPanel = (props: any) => {
  /**
   * Access control
   */
  const [isUserAdmin, userAccessLevel] = useGetCurrentPageUserAccessLevel()
  const hasWriteAccess = useHasWriteAccess()

  const {developmentStageId, vehicleProgramId, adasProgramId} = useGetUrlParams()
  const dispatch = useAppDispatch()
  /**
   * Horizontal tabs
   */
  const [htabs, setHtabs] = useState<any[]>([])
  useEffect(() => {
    const tab1 = {
      name: "Vehicle Measurements",
      content: <VehicleMeasurements/>
    }
    const tab2 = {
      name: 'Sensor Information',
      content: <SensorInformation/>
    }
    setHtabs([tab1, tab2])
  }, [])

  /**
   * Wizard updates this state when it submits. We can listen to it here to refresh.
   * TODO: We can change the selected node if a new development stage is added. If it wasn't a new development stage, we don't need to refresh.
   */
  const refreshAfterNewDevelopmentStageAdded = useSelectRefreshTableCount()

  /**
   * Development stage data
   */
  const developmentStageData = useAppSelector(selectVehicleProgramsSelectedDevelopmentStageData)
  const getDevelopmentStageDataHandle = usePostRequest(VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.getDevelopmentStageData)
  useEffect(() => {
    if (!developmentStageId || !vehicleProgramId || adasProgramId != null) return
    dispatch(setIsLoadingDevelopmentStageData(true))
    getDevelopmentStageDataHandle({developmentStageId, vehicleProgramId}).then((res: any) => {
      dispatch(setVehicleProgramsSelectedDevelopmentStageData(res))
    }).catch((err: any) => {
      console.log('error', err)
      dispatch(setVehicleProgramsSelectedDevelopmentStageData(null))
    }).finally(() => {
      dispatch(setIsLoadingDevelopmentStageData(false))
    })
  }, [developmentStageId, vehicleProgramId, refreshAfterNewDevelopmentStageAdded, adasProgramId]);


  const {instance, accounts} = useMsal()

  const downloadSheet = async () => {
    const vehicle = developmentStageData?.vehicle_measurements
    const vehicle_program_name = vehicle.vehicle_program.replace(" ", "_")
    const vehicle_program_names = vehicle_program_name.replace(" ", "_")
    const fileName = developmentStageData?.source_file_name
    const downloadRequest = {
      fileName
    }

    let url = `${config.url.API_URL}${VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.downloadDevelopmentStageFile}`
    const idTok = await requestIdToken(instance, accounts)
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(downloadRequest)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      res.blob().then((blob: any) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a')
        alink.href = fileURL;
        alink.download = `${vehicle_program_names}_${vehicle.vehicle_program_development_stage}_` + fileName
        alink.click();
      })
    }).catch((e) => {
      console.log(e)
    })
  }
/**
 * Download CSV
 *
 * @description This function downloads the CSV file of the vehicle program development stage.
 * It uses the fetch API to make a POST request to the server with the file name and development stage ID.
 * The response is then converted to a blob and a link is created to download the file.
 * The file name is generated based on the vehicle program name and development stage.
 * The file is downloaded with the name format: {vehicle_program_name}_{development_stage}_{file_name}.csv
 * @error {any} - If there is an error during the fetch request, it will be logged to the console.
 */
  const downloadCsv = async () => {
    const vehicle = developmentStageData?.vehicle_measurements
    const vehicle_program_names = vehicle.vehicle_program.replace(" ", "_")
    const fileName = developmentStageData?.source_file_name

    const downloadRequest = {
      fileName,
      developmentStageId,
      vehicleProgramId
    }
    let url = `${config.url.API_URL}${VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.downloadVehicleProgramCsvFile}`
    const idTok = await requestIdToken(instance, accounts)
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(downloadRequest)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      res.blob().then((blob: any) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a')
        alink.href = fileURL;
        alink.download = `${vehicle_program_names}_${vehicle.vehicle_program_development_stage}_` + fileName.replace(".xlsx", ".csv")
        alink.click();
      })
    }).catch((e) => {
      console.log(e)
    })
  }
  /**
   * Download JSON
   *
   * @description This function downloads the JSON file of the vehicle program development stage.
   * It uses the previously stored vehicleMeasurements and sensorInformation json objects, concatenates them together and a link is created to download the file.
   * The file name is generated based on the vehicle program name and development stage.
   * The file is downloaded with the name format: {vehicle_program_name}_{development_stage}_{file_name}.json
   */
  const downloadJSON = async () => {
    const vehicle = developmentStageData?.vehicle_measurements
    const vehicle_program_names = vehicle.vehicle_program.replace(" ", "_")
    const vehicleMeasurements = developmentStageData?.vehicle_measurements?.dimensionsListTableData
    const sensorInformation = developmentStageData?.sensor_positions_table_data
    const fileName = developmentStageData?.source_file_name

    const jsonData = JSON.stringify({
      vehicleMeasurements,
      sensorInformation
    }, null, 2)

    const blob = new Blob([jsonData], { type: "application/json" })
    const fileURL = window.URL.createObjectURL(blob);

    const alink = document.createElement("a")
    alink.href = fileURL
    alink.download =  `${vehicle_program_names}_${vehicle.vehicle_program_development_stage}_` + fileName.replace(".xlsx", ".json")
    alink.click()
  }

  const handleClickConfig = () => {
    dispatch(setIsConfigSensorReferenceFrameDialogOpen(true))
  }

  /**
   * Recently viewed
   */
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid: developmentStageId,
    partitionKey: vehicleProgramId,
    trackedItemType: ETrackedItemTypes.VEHICLE_PROGRAM_DEVELOPMENT_STAGE,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)

  return (
    <ArgosFlexbox vertical style={{width: "100%", height: "100%", background: '#ffffff', overflow: 'auto'}}>
      {(developmentStageId != null && adasProgramId == null) &&
        <div style={{display: developmentStageData != null ? 'block' : 'none'}}>
          <div style={{marginLeft: 'auto'}}>
            <ArgosFlexbox alignItems='center' style={{justifyContent: 'right'}}>
              <IconButton
                onClick={() => {
                  handleClickConfig()
                }}
                hidden={true} // TODO: Remove this line when the feature is ready
              >
                <ArgosSettingsIcon/>
              </IconButton>
              <ArgosPopper
                button={
                <IconButton>
                  <ArgosDownloadIcon fontSize="medium" style={{color: tableButtonGray}}/>
                </IconButton>
                }
                tooltip={
                 <Tooltip title="Download" arrow placement="bottom" children={<></>}/>
                }
                children={
                  <ArgosFlexbox vertical display='block' alignItems='start'>
                    {checkDownloadMenuItemPermission(isUserAdmin, userAccessLevel, hasWriteAccess, EDownloadMenuItemType.Spreadsheet) && <ArgosMenuItem
                      sx={{p: 1, color: baseBlue, bgcolor: 'background.paper'}}
                      onClick={(e) => {
                        downloadSheet()
                      }}
                    >
                      Spreadsheet
                    </ArgosMenuItem>}
                    {checkDownloadMenuItemPermission(isUserAdmin, userAccessLevel, hasWriteAccess, EDownloadMenuItemType.CSV) &&  <ArgosMenuItem
                      sx={{p: 1, color: baseBlue, bgcolor: 'background.paper'}}
                       onClick={(e) => {
                        downloadCsv()
                      }}
                    >
                      CSV
                    </ArgosMenuItem>}
                    {checkDownloadMenuItemPermission(isUserAdmin, userAccessLevel, hasWriteAccess, EDownloadMenuItemType.JSON) &&  <ArgosMenuItem
                      sx={{p: 1, color: baseBlue, bgcolor: 'background.paper'}}
                       onClick={(e) => {
                        downloadJSON()
                        }}
                    >
                      JSON
                    </ArgosMenuItem>}
                  </ArgosFlexbox>
                }
              />
              <ArgosFavoriteIconLogic favoriteType={ETrackedItemTypes.VEHICLE_PROGRAM_DEVELOPMENT_STAGE} trackedItemUid={developmentStageId} partitionKey={vehicleProgramId}/>
            </ArgosFlexbox>
          </div>
          <div style={{flex: 1}}>
            <HorizontalTabs initContent={null} initHtabIndex={0} horizontalTabs={htabs} align={'left'}
                            uid={'htabs-dev-stage'}

            />
          </div>
        </div>
      }
      {
        adasProgramId != null &&
        <AdasItemDetails/>
      }
    </ArgosFlexbox>
  )
}

export default RightPanel